import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { downloadAttachment } from '../../utils'

import { getCatalog } from '../../api/catalog'

import { useWindowResize } from '../../hooks/useWindowResize'

import { CenterItem } from '../../components/common'
import { Text, StyledText } from '../../components/text'
import { Spinner } from '../../components/spinner/Spinner'
import { ArtipioContents, ArtipioContentItem } from '../../components/contents'
import { CatalogImagesModal } from './modal/CatalogImagesModal'

import { Color } from '../../theme/color'

import { ReactComponent as ExclamationIcon } from '../../assets/gui/exclamation_90.svg'
import { ReactComponent as DownloadIcon } from '../../assets/ico/common/download_black_18.svg'

import '../../theme/catalog.css'

export const MAX_WIDTH = 800 // catalog view 최대 가로 폭
const MAX_HEIGHT = 700 // thumbnail image 최대 높이

export function Catalog() {
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0)
  const [modalShow, setModalShow] = useState<boolean>(false)

  const location = useLocation()
  const isBreak = useWindowResize(MAX_WIDTH)

  const uriKey = location.pathname.split('/').pop() || ''

  const {
    data: catalog,
    isPending,
    isError,
  } = useQuery({ queryKey: [QUERY_KEY.catalog, { uriKey }], queryFn: () => getCatalog(uriKey) })

  const toggleModal = () => setModalShow((prevState) => !prevState)

  const downloadProductWarranty = async () => {
    if (!catalog?.files) return

    const file = catalog.files.find((file) => file.catalogFileItemType === 'DOCS_PRODUCT_WARRANTY')?.fileItem
    if (!file) return

    await downloadAttachment(file.filePath ?? '', file?.filename)
  }

  const downloadConditionReport = async () => {
    if (!catalog?.files) return

    const file = catalog.files.find((file) => file.catalogFileItemType === 'DOCS_CONDITION_REPORT')?.fileItem
    if (!file) return

    await downloadAttachment(file.filePath ?? '', file?.filename)
  }

  if (isPending) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    )
  }

  if (isError)
    return (
      <ErrorWrap>
        <CenterItem $direction="column" $gap={24}>
          <CenterItem $direction="column" $gap={16}>
            <ExclamationIcon />
            <Text size={24} weight={600} lineHeight={34}>
              Something went wrong
            </Text>
            <Text
              size={14}
              weight={500}
              lineHeight={20}
              color="text_secondary"
              $textAlign="center"
              $wordBreak="keep-all"
            >
              This might be a broken link or an invalid request.{isBreak ? ' ' : <br />}We’re very sorry for the
              inconvenience.
            </Text>
          </CenterItem>
        </CenterItem>
      </ErrorWrap>
    )

  if (modalShow && catalog.images) {
    return <CatalogImagesModal images={catalog.images} activeImageIndex={activeImageIndex} toggleModal={toggleModal} />
  }

  return (
    <Wrap isBreak={isBreak}>
      <Header>
        <Text size={50} weight={700} lineHeight={60}>
          {catalog.artist}
        </Text>
        <Text size={30} weight={700} lineHeight={36}>
          {catalog.name}
        </Text>
      </Header>
      <Thumbnail onClick={toggleModal}>
        <img src={catalog.images?.[activeImageIndex].filePath} alt={catalog.images?.[activeImageIndex].filename} />
      </Thumbnail>
      <Images>
        {catalog.images?.map(({ id, filePath, filename }, index) => {
          const isActive = activeImageIndex === index

          const selectImage = () => setActiveImageIndex(index)

          return (
            <ImageItem key={id} isActive={isActive} onClick={selectImage}>
              <img src={filePath} alt={filename} />
            </ImageItem>
          )
        })}
      </Images>
      {catalog.files?.length ? (
        <ButtonBox>
          {catalog.files.map(({ catalogFileItemType }) => {
            const buttonProps = () => {
              switch (catalogFileItemType) {
                case 'DOCS_CONDITION_REPORT':
                  return { label: 'Condition Report.pdf', download: downloadConditionReport }
                case 'DOCS_PRODUCT_WARRANTY':
                  return { label: 'Certificate of authenticity.pdf', download: downloadProductWarranty }
                default:
                  return { label: '', download: () => alert('잘못 된 접근입니다.') }
              }
            }

            return (
              <FileButton key={catalogFileItemType} onClick={buttonProps().download}>
                {buttonProps().label}
                <DownloadIcon />
              </FileButton>
            )
          })}
        </ButtonBox>
      ) : null}
      {catalog.catalogPriceDisplayStatus === 'SHOW' ? (
        <Estimate size={24} weight={600} block={true}>
          Estimate: ${catalog.sellPrice}
        </Estimate>
      ) : null}
      <Divider />
      <ArtipioContents isPadding={false}>
        {catalog.contents?.map((props, index) => (
          <ArtipioContentItem key={index} {...props} />
        ))}
      </ArtipioContents>
    </Wrap>
  )
}

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
`

const ErrorWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 800px;
  margin: 0 auto;
  padding: 16px;
`

const Wrap = styled.div<{ isBreak: boolean }>`
  max-width: ${MAX_WIDTH}px;
  margin: 0 auto;
  padding: 100px ${({ isBreak }) => (isBreak ? '16px' : '0')} 140px;
  ${({ isBreak }) =>
    isBreak
      ? `
        ${Header} > * {
          text-align: left;
        }
        ${Images} {
          gap: calc((100% - 32px) / 70);
        }
        ${ButtonBox} {
          flex-direction: column;
        }
      `
      : ''}
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
  > * {
    text-align: center;
    word-break: keep-all;
  }
`

const Thumbnail = styled.div`
  overflow: hidden;
  width: 100%;
  max-height: ${MAX_HEIGHT}px;
  margin-bottom: 16px;
  text-align: center;
  cursor: pointer;
  > img {
    width: 100%;
    height: 100%;
    max-height: ${MAX_HEIGHT}px;
    object-fit: contain;
  }
`

const Images = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: calc(100% / 70);
  margin-bottom: 16px;
`

const ImageItem = styled.div<{ isActive: boolean }>`
  overflow: hidden;
  aspect-ratio: 1/1;
  ${({ isActive }) => (isActive ? `border: 2px solid ${Color.primary_5};` : '')}
  cursor: pointer;
  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const ButtonBox = styled.div`
  display: flex;
  gap: 16px;
`

const Estimate = styled(StyledText)`
  margin-top: 24px;
`

const FileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 12px;
  border: none;
  border-radius: 16px;
  background-color: ${Color.gray_2};
  font-size: 16px;
  font-weight: 700;
`

const Divider = styled.div`
  width: 100%;
  height: 0;
  margin: 24px 0;
  border-bottom: 1px solid ${Color.text_secondary};
`
